const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4000';

const TOKEN_KEY = 'ndule.admin.tokens.v1';

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

export function getTokens(): TokenPair | null {
  const raw = localStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as TokenPair;
  } catch {
    return null;
  }
}

export function setTokens(t: TokenPair): void {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(t));
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
}

let refreshing: Promise<void> | null = null;
async function refreshTokens(): Promise<boolean> {
  if (refreshing) {
    await refreshing;
    return Boolean(getTokens()?.accessToken);
  }
  refreshing = (async () => {
    const t = getTokens();
    if (!t) return;
    try {
      const r = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: t.refreshToken }),
      });
      if (!r.ok) {
        clearTokens();
        return;
      }
      const body = await r.json();
      setTokens(body.tokens);
    } catch {
      clearTokens();
    }
  })();
  await refreshing;
  refreshing = null;
  return Boolean(getTokens()?.accessToken);
}

export async function api<T = unknown>(path: string, init: RequestInit & { auth?: boolean } = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (init.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  let attached = false;
  if (init.auth !== false) {
    const t = getTokens();
    if (t) {
      headers.Authorization = `Bearer ${t.accessToken}`;
      attached = true;
    }
  }
  let r = await fetch(`${API_URL}${path}`, { ...init, headers });
  if (r.status === 401 && attached && (await refreshTokens())) {
    headers.Authorization = `Bearer ${getTokens()!.accessToken}`;
    r = await fetch(`${API_URL}${path}`, { ...init, headers });
  }
  if (r.status === 204) return undefined as T;
  const json = await r.json().catch(() => null);
  if (!r.ok) {
    const err = new Error(json?.error?.message ?? r.statusText) as Error & { status?: number };
    err.status = r.status;
    throw err;
  }
  return json as T;
}
